import { useState } from 'react';
import { useQuery } from 'react-query';
import { reportsApi, projectsApi } from '@/services/api';
import { Search, Ban, Calendar, BarChart3, AlertTriangle, Download, FileText, RefreshCw, PartyPopper, Info } from 'lucide-react';
import { formatDateTime } from '@/utils/dateFormat';

const ReportsPage = () => {
  const [selectedProject, setSelectedProject] = useState<string>('');

  // Dropout Stats Query
  const { data: dropoutStats, isLoading: dropoutStatsLoading, refetch: refetchDropoutStats } = useQuery(
    'dropoutStats',
    () => reportsApi.getDropoutStats(),
    {
      refetchInterval: 30000,
      onError: (error) => {
        console.error('Dropout stats error:', error);
      },
      retry: false,
    }
  );

  // Dropout Logs Query
  const { data: dropoutLogs, isLoading: dropoutLogsLoading } = useQuery(
    ['dropoutLogs', selectedProject],
    () => reportsApi.getDropoutLogs({
      projectId: selectedProject || undefined,
      limit: 100
    }),
    {
      refetchInterval: 30000,
      onError: (error) => {
        console.error('Dropout logs error:', error);
      },
      retry: false,
    }
  );

  // Projects List for dropdown
  const { data: projectsList } = useQuery(
    'projects',
    () => projectsApi.getAll(),
    {
      select: (data: any) => data.data || [],
      onError: (error) => {
        console.error('Projects list error:', error);
      },
      retry: false,
    }
  );

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const handleDropoutExport = async () => {
    try {
      const exportParams = {
        format: 'csv',
        ...(selectedProject && { projectId: selectedProject }),
      };

      const blob = await reportsApi.exportDropoutData(exportParams as any);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dropout-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert('Dropout data exported successfully!');
    } catch (error) {
      console.error('Dropout export failed:', error);
      alert('Dropout export failed. Please try again.');
    }
  };

  const handleDropoutExportJSON = async () => {
    try {
      const exportParams = {
        format: 'json',
        ...(selectedProject && { projectId: selectedProject }),
      };

      const result = await reportsApi.exportDropoutData(exportParams as any);

      const jsonStr = JSON.stringify(result, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dropout-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert('Dropout data exported successfully!');
    } catch (error) {
      console.error('Dropout export failed:', error);
      alert('Dropout export failed. Please try again.');
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-end animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1F2B] mb-1">Dropout Detection Reports</h1>
          <p className="text-[#6B7280] text-[15px]">
            Monitor unexpected page hits and identify potential issues
          </p>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="md-card p-6 animate-fade-in-up stagger-1">
        <div className="space-y-6">
          <h3 className="text-lg font-bold font-heading text-[#1A1F2B] flex items-center gap-2">
            <Search className="w-5 h-5 text-[#18A5F9]" />
            Dropout Detection Reports
          </h3>

          {/* Overview Stats */}
          {dropoutStatsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#18A5F9]"></div>
              <span className="ml-3 text-[#6B7280]">Loading dropout data...</span>
            </div>
          ) : dropoutStats?.data ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-bold text-orange-600 uppercase tracking-wider">Total Dropouts</p>
                    <p className="text-2xl font-bold text-orange-900 mt-1">{formatNumber(dropoutStats.data.total)}</p>
                  </div>
                  <Ban className="w-8 h-8 text-orange-400" />
                </div>
              </div>

              <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-bold text-red-600 uppercase tracking-wider">Today</p>
                    <p className="text-2xl font-bold text-red-900 mt-1">{formatNumber(dropoutStats.data.today)}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-red-400" />
                </div>
              </div>

              <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-bold text-amber-600 uppercase tracking-wider">This Week</p>
                    <p className="text-2xl font-bold text-amber-900 mt-1">{formatNumber(dropoutStats.data.thisWeek)}</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-amber-400" />
                </div>
              </div>

              <div className="bg-[#E8F4FD] p-4 rounded-xl border border-[#B3DCFA]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-bold text-[#0B7BC7] uppercase tracking-wider">Recent Activity</p>
                    <p className="text-2xl font-bold text-[#0B7BC7] mt-1">{dropoutStats.data.recentActivity?.length || 0}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-[#18A5F9]/40" />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-[#9CA3AF]">
              No dropout data available
            </div>
          )}

          {/* Project Filter & Actions */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Filter by Project</label>
              <div className="md-outlined-textfield">
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="w-full"
                >
                  <option value="">All Projects</option>
                  {(projectsList || []).map((project: any) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Export Buttons */}
            <div className="flex gap-2 items-end">
              <button
                onClick={handleDropoutExport}
                className="md-btn md-btn-primary text-[13px] flex items-center gap-1.5"
              >
                <Download className="w-4 h-4" /> CSV
              </button>

              <button
                onClick={handleDropoutExportJSON}
                className="md-btn md-btn-outlined text-[13px] flex items-center gap-1.5"
              >
                <FileText className="w-4 h-4" /> JSON
              </button>

              <button
                onClick={() => refetchDropoutStats()}
                className="md-btn md-btn-outlined text-[13px] flex items-center gap-1.5"
              >
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
            </div>
          </div>

          {/* Recent Dropouts Table */}
          {dropoutLogsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#18A5F9]"></div>
              <span className="ml-3 text-[#6B7280]">Loading dropout logs...</span>
            </div>
          ) : dropoutLogs?.data?.logs && dropoutLogs.data.logs.length > 0 ? (
            <div>
              <h4 className="font-bold text-[#1A1F2B] mb-3">Recent Unknown URL Hits</h4>
              <div className="overflow-x-auto rounded-xl border border-[#ECEEF1]">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-[#F9FAFB] border-b border-[#ECEEF1]">
                      <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Time</th>
                      <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">URL</th>
                      <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Method</th>
                      <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Query Params</th>
                      <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Project</th>
                      <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">IP Address</th>
                      <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">User Agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dropoutLogs.data.logs.map((dropout: any) => (
                      <tr key={dropout.id} className="border-b border-[#ECEEF1] hover:bg-[#F9FAFB] transition-colors">
                        <td className="py-3 px-4 text-sm text-[#374151] whitespace-nowrap">
                          {formatDateTime(dropout.timestamp)}
                        </td>
                        <td className="py-3 px-4 text-sm font-mono text-[#EF4444]">
                          {dropout.url}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <span className="px-2 py-0.5 bg-[#F3F5F7] text-[#6B7280] rounded-full text-xs font-semibold border border-[#ECEEF1]">
                            {dropout.method || 'GET'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {dropout.query && Object.keys(dropout.query).length > 0 ? (
                            <details className="cursor-pointer">
                              <summary className="text-[#18A5F9] hover:text-[#0D8FDE] font-semibold text-[13px]">
                                View ({Object.keys(dropout.query).length})
                              </summary>
                              <pre className="mt-2 p-2 bg-[#F9FAFB] rounded-lg text-xs overflow-x-auto border border-[#ECEEF1]">
                                {JSON.stringify(dropout.query, null, 2)}
                              </pre>
                            </details>
                          ) : (
                            <span className="text-[#9CA3AF]">&mdash;</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-[#374151]">
                          {dropout.detectedProject?.name ||
                           (dropout.projectid ? `ID: ${dropout.projectid}` :
                            <span className="text-[#9CA3AF]">Unknown</span>)}
                        </td>
                        <td className="py-3 px-4 text-sm font-mono text-[#6B7280]">
                          {dropout.ipAddress || <span className="text-[#9CA3AF]">&mdash;</span>}
                        </td>
                        <td className="py-3 px-4 text-sm max-w-xs truncate text-[#6B7280]" title={dropout.userAgent}>
                          {dropout.userAgent || <span className="text-[#9CA3AF]">&mdash;</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {dropoutLogs.data.logs.length > 50 && (
                <p className="text-[13px] text-[#9CA3AF] text-center mt-4 font-medium">
                  Showing {Math.min(50, dropoutLogs.data.logs.length)} of {dropoutLogs.data.logs.length} total dropouts
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-[#6B7280]">
              <div className="flex justify-center mb-2">
                <PartyPopper className="w-12 h-12 text-[#18A5F9]" />
              </div>
              <p className="font-medium">No dropout URLs detected. All traffic is flowing correctly!</p>
            </div>
          )}

          {/* Top Problem URLs */}
          {dropoutStats?.data?.topUrls && dropoutStats.data.topUrls.length > 0 && (
            <div className="mt-6">
              <h4 className="font-bold text-[#1A1F2B] mb-3">Top Problem URLs</h4>
              <div className="space-y-2">
                {dropoutStats.data.topUrls.slice(0, 10).map((item: any, idx: number) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-red-50 rounded-xl border border-red-100">
                    <span className="text-sm font-mono text-red-800 flex-1 truncate" title={item.url}>
                      {item.url}
                    </span>
                    <span className="text-sm font-bold text-[#EF4444] ml-4 whitespace-nowrap">
                      {item.count} hit{item.count !== 1 ? 's' : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info */}
          <div className="mt-6 p-4 bg-[#E8F4FD] rounded-xl border border-[#B3DCFA]">
            <h4 className="font-bold text-[#0B7BC7] mb-2 flex items-center gap-2">
              <Info className="w-5 h-5" />
              What are Dropouts?
            </h4>
            <ul className="text-sm text-[#0B7BC7] space-y-1">
              <li>&bull; <strong>Dropouts</strong> are page hits to URLs that don't match our tracking endpoints (/complete, /terminate, /quota, /security)</li>
              <li>&bull; This helps identify broken links, incorrect URLs, or respondents accessing wrong pages</li>
              <li>&bull; All query parameters, IP addresses, and user agents are captured for troubleshooting</li>
              <li>&bull; CSV/JSON exports include complete details for analysis</li>
              <li>&bull; Data refreshes automatically every 30 seconds</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
